import type { VisionClient } from './client'
import type { Order, OrderInventory, OrderStatus, ValidationRun } from './schemas'

/**
 * Seeded local data for reviewing the bulk-allocation interaction without a running backend.
 * Production can never enable this because env.ts gates the flag on import.meta.env.DEV.
 */
export function withBulkDemoData(client: VisionClient): VisionClient {
  const overrides: Partial<VisionClient> = {
    getMe: async () => ({
      subject: 'bulk-ui-demo',
      email: 'hari@boldfit.in',
      displayName: 'Hari · Bulk UI demo',
      role: 'BoldfitManager',
      }),
    listTenants: async () => ({
      items: [
        {
          id: '10000000-0000-4000-8000-000000000001',
          code: 'bbpl-main',
          displayName: 'Boldfit',
          isActive: true,
          createdAt: '2026-01-01T00:00:00Z',
        },
      ],
    }),
    listChannels: async () => ({
      items: [
        channel('amazon', 'Amazon'),
        channel('flipkart', 'Flipkart'),
        channel('blinkit', 'Blinkit'),
      ],
    }),
    listVendors: async () => ({
      items: [
        vendor('amz-in-vendor-1', 'Amazon Retail India'),
        vendor('flipkart', 'Flipkart India'),
        vendor('blinkit', 'Blink Commerce'),
      ],
    }),
    listWarehouses: async () => ({
      items: [
        { code: 'RGB', name: 'RGL Gurugram', preferred: true, regionCode: 'NORTH' },
        { code: 'L01', name: 'Karnataka Main', preferred: false, regionCode: 'SOUTH' },
        { code: 'RBB', name: 'RGL Bengaluru', preferred: false, regionCode: 'SOUTH' },
        { code: 'AJT', name: 'Ajitgarh', preferred: false, regionCode: 'NORTH' },
        { code: 'L04', name: 'Maharashtra Main', preferred: false, regionCode: 'WEST' },
      ],
    }),
    listOrders: async params => {
      const needle = params.q?.trim().toLowerCase()
      const matching = DEMO_ORDERS.filter(order => {
        if (needle && !order.externalOrderNumber.toLowerCase().includes(needle)) return false
        if (
          params.channel &&
          !(Array.isArray(params.channel)
            ? params.channel.includes(order.channel)
            : order.channel === params.channel)
        )
          return false
        if (
          params.vendorCode &&
          !(Array.isArray(params.vendorCode)
            ? params.vendorCode.some(code => order.vendor?.toLowerCase() === code.toLowerCase())
            : order.vendor?.toLowerCase() === params.vendorCode.toLowerCase())
        )
          return false
        if (params.status?.length && !params.status.includes(order.status)) return false
        return true
      })
      const page = params.page ?? 0
      const size = params.size ?? 25
      return {
        items: matching.slice(page * size, (page + 1) * size),
        page,
        size,
        total: matching.length,
      }
    },
    getOrdersSummary: async () => ({
      poCount: DEMO_ORDERS.length,
      orderedQty: 456,
      orderedValue: 397_200,
      sentToWarehouseQty: 48,
      sentToWarehouseValue: 31_200,
      remainingQty: 408,
      remainingValue: 366_000,
      shippedQty: 48,
      shippedValue: 31_200,
      pendingQty: 408,
      pendingValue: 366_000,
    }),
    getOrder: async externalOrderNumber => findOrder(externalOrderNumber),
    getValidation: async externalOrderNumber =>
      DEMO_VALIDATION[externalOrderNumber] ?? passedValidation(findOrder(externalOrderNumber)),
    getOrderInventory: async externalOrderNumber =>
      DEMO_INVENTORY[externalOrderNumber] ?? { lines: [], inventoryUnavailable: false },
    getAudit: async externalOrderNumber => ({
      externalOrderNumber,
      tenantId: 'bbpl-main',
      events: [],
    }),
    listItems: async params => ({
      items: params.channelItemCodes.map(code => ({
        channel: params.channel,
        channelItemCode: code,
        mappedSku: code.startsWith('B0') ? `BF-${code.slice(-5)}` : code,
        title: demoTitle(code),
        imageUrl: null,
        brand: 'Boldfit',
        category: 'Fitness',
        updatedAt: '2026-09-07T09:00:00Z',
      })),
    }),
  }
  return { ...client, ...overrides }
}

function channel(code: string, displayName: string) {
  return {
    id:
      code === 'amazon'
        ? '20000000-0000-4000-8000-000000000001'
        : code === 'flipkart'
          ? '20000000-0000-4000-8000-000000000002'
          : '20000000-0000-4000-8000-000000000003',
    tenantId: '10000000-0000-4000-8000-000000000001',
    code,
    displayName,
    channel: code,
    marketplace: displayName,
    merchantId: null,
    logoUrl: null,
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  }
}

function vendor(code: string, displayName: string) {
  return {
    id:
      code === 'amz-in-vendor-1'
        ? '30000000-0000-4000-8000-000000000001'
        : code === 'flipkart'
          ? '30000000-0000-4000-8000-000000000002'
          : '30000000-0000-4000-8000-000000000003',
    tenantId: '10000000-0000-4000-8000-000000000001',
    code,
    displayName,
    legalName: displayName,
    gstin: null,
    isActive: true,
    createdAt: '2026-01-01T00:00:00Z',
  }
}

function findOrder(externalOrderNumber: string): Order {
  const order = DEMO_ORDERS.find(row => row.externalOrderNumber === externalOrderNumber)
  if (!order) throw new Error(`Demo PO ${externalOrderNumber} not found`)
  return order
}

function passedValidation(order: Order): ValidationRun {
  return {
    id: `demo-validation-${order.id}`,
    orderId: order.id,
    result: 'PASSED',
    issues: [],
    ranAt: '2026-09-07T12:30:00Z',
  }
}

function demoTitle(code: string): string {
  const titles: Record<string, string> = {
    B0BULK1001: 'Boldfit Adjustable Hand Grip',
    B0BULK1002: 'Boldfit Gym Gloves',
    'FK-SKU-731': 'Boldfit Yoga Mat 6mm',
    'FK-SKU-732': 'Boldfit Resistance Tube Set',
    '8909136002280': 'Boldfit Shaker Bottle',
  }
  return titles[code] ?? `Boldfit product ${code}`
}

function item(
  id: string,
  itemSequenceNumber: number,
  channelSku: string,
  quantity: number,
  unitCost: number,
  options: { fulfilled?: number; caseSize?: number } = {},
) {
  const fulfilled = options.fulfilled ?? 0
  return {
    id,
    itemSequenceNumber,
    channelSku,
    displayName: demoTitle(channelSku),
    orderedQuantity: quantity,
    unitOfMeasure: 'Eaches',
    netAmount: { amount: unitCost, currency: 'INR' },
    caseSize: options.caseSize ?? 12,
    ackCode: 'Accepted',
    ackQuantity: quantity,
    fulfilledQuantity: fulfilled,
    remainingToWarehouse: quantity - fulfilled,
  }
}

const DEMO_ORDERS: Order[] = [
  {
    id: '41000000-0000-4000-8000-000000000001',
    tenantId: 'bbpl-main',
    channel: 'amazon',
    externalOrderNumber: 'PO-AMZ-24091',
    vendor: 'amz-in-vendor-1',
    vendorName: 'Amazon Retail India',
    shipToLocationCode: 'FHY3',
    preferredHubs: ['RGB', 'AJT'],
    orderedAt: '2026-09-05T05:30:00Z',
    createdAt: '2026-09-05T05:34:00Z',
    status: 'FULLY_ACKED',
    items: [
      item('51000000-0000-4000-8000-000000000001', 1, 'B0BULK1001', 96, 640, {
        caseSize: 24,
      }),
      item('51000000-0000-4000-8000-000000000002', 2, 'B0BULK1002', 48, 920, {
        caseSize: 12,
      }),
    ],
    remainingQty: 144,
    orderedQty: 144,
    orderedValuePaise: 10_560_000,
    remainingValuePaise: 10_560_000,
  },
  {
    id: '41000000-0000-4000-8000-000000000002',
    tenantId: 'bbpl-main',
    channel: 'flipkart',
    externalOrderNumber: 'FLS-73112',
    vendor: 'flipkart',
    vendorName: 'Flipkart India',
    shipToLocationCode: 'BLR',
    shippedFrom: 'VS53418',
    preferredHubs: ['L01', 'RBB'],
    orderedAt: '2026-09-06T04:00:00Z',
    createdAt: '2026-09-06T04:02:00Z',
    status: 'ACKNOWLEDGED',
    items: [
      {
        ...item('51000000-0000-4000-8000-000000000003', 1, 'FK-SKU-731', 120, 780, {
          caseSize: 10,
        }),
        ackCode: null,
        ackQuantity: null,
      },
      {
        ...item('51000000-0000-4000-8000-000000000004', 2, 'FK-SKU-732', 60, 1_250, {
          caseSize: 10,
        }),
        ackCode: null,
        ackQuantity: null,
      },
    ],
    remainingQty: 180,
    orderedQty: 180,
    orderedValuePaise: 16_860_000,
    remainingValuePaise: 16_860_000,
  },
  {
    id: '41000000-0000-4000-8000-000000000003',
    tenantId: 'bbpl-main',
    channel: 'amazon',
    externalOrderNumber: 'PO-AMZ-24107',
    vendor: 'amz-in-vendor-1',
    vendorName: 'Amazon Retail India',
    shipToLocationCode: 'FHY3',
    preferredHubs: ['RGB'],
    orderedAt: '2026-09-06T08:15:00Z',
    createdAt: '2026-09-06T08:18:00Z',
    status: 'PARTIALLY_FULFILLED',
    items: [
      item('51000000-0000-4000-8000-000000000005', 1, 'B0BULK1001', 96, 640, {
        fulfilled: 48,
        caseSize: 24,
      }),
    ],
    remainingQty: 48,
    orderedQty: 96,
    orderedValuePaise: 6_144_000,
    sentToWarehouseQty: 48,
    sentToWarehouseValuePaise: 3_072_000,
    remainingValuePaise: 3_072_000,
  },
  {
    id: '41000000-0000-4000-8000-000000000004',
    tenantId: 'bbpl-main',
    channel: 'blinkit',
    externalOrderNumber: '49989910031154',
    vendor: 'blinkit',
    vendorName: 'Blink Commerce',
    shipToLocationCode: 'Pune P3',
    preferredHubs: ['L04'],
    orderedAt: '2026-09-07T02:30:00Z',
    createdAt: '2026-09-07T02:32:00Z',
    status: 'FULLY_ACKED',
    items: [
      {
        ...item('51000000-0000-4000-8000-000000000006', 1, '8909136002280', 84, 360, {
          caseSize: 12,
        }),
        ackCode: null,
        ackQuantity: null,
      },
    ],
    remainingQty: 84,
    orderedQty: 84,
    orderedValuePaise: 3_024_000,
    remainingValuePaise: 3_024_000,
  },
]

const DEMO_VALIDATION: Record<string, ValidationRun> = {
  'PO-AMZ-24107': {
    id: 'demo-validation-price-hold',
    orderId: '41000000-0000-4000-8000-000000000003',
    result: 'FAILED',
    ranAt: '2026-09-07T12:30:00Z',
    issues: [
      {
        code: 'PRICE_MISMATCH',
        orderItemId: '51000000-0000-4000-8000-000000000005',
        severity: 'FAIL',
        blocking: true,
        message: 'Line 1 stated ₹640 vs agreed ₹590',
      },
    ],
  },
}

const DEMO_INVENTORY: Record<string, OrderInventory> = {
  'PO-AMZ-24091': inventory([
    lineInventory(1, [stock('RGB', 80, true), stock('AJT', 36, true), stock('L01', 120, false)]),
    lineInventory(2, [stock('RGB', 0, true), stock('AJT', 0, true), stock('L01', 72, false)]),
  ]),
  'FLS-73112': inventory([
    lineInventory(1, [stock('L01', 90, true), stock('RBB', 60, true), stock('RGB', 40, false)]),
    lineInventory(2, [stock('L01', 60, true), stock('RBB', 30, true), stock('RGB', 20, false)]),
  ]),
  'PO-AMZ-24107': inventory([lineInventory(1, [stock('RGB', 80, true), stock('AJT', 36, false)])]),
  '49989910031154': inventory([
    lineInventory(1, [stock('L04', 84, true), stock('RGB', 24, false)]),
  ]),
}

function inventory(lines: OrderInventory['lines']): OrderInventory {
  return {
    lines,
    channelClass: 'ECOM',
    asOf: '2026-09-07T12:25:00Z',
    newestUpdatedAt: '2026-09-07T12:29:00Z',
    staleRows: 0,
    neverObservedRows: 0,
    stalenessHorizon: 'PT24H',
    inventoryUnavailable: false,
  }
}

function lineInventory(
  itemSequenceNumber: number,
  byWarehouse: OrderInventory['lines'][number]['byWarehouse'],
) {
  return {
    itemSequenceNumber,
    totalAtp: byWarehouse.reduce((sum, row) => sum + row.atp, 0),
    byWarehouse,
  }
}

function stock(code: string, atp: number, preferred: boolean) {
  return {
    code,
    atp,
    preferred,
    available: atp,
    qty: atp,
    committedQty: 0,
    mslShare: 0,
    regionCode: code === 'RGB' || code === 'AJT' ? 'NORTH' : 'SOUTH',
    regionMsl: 0,
    updatedAt: '2026-09-07T12:28:00Z',
    observed: true,
  }
}

void (DEMO_ORDERS satisfies Array<Order & { status: OrderStatus }>)
